import type { RAFile } from "../../types";
import type { EmailDraft } from "../ComposeEmailDialog";
import { replySubject } from "../emailTemplates";
import type { MailboxMessage } from "../mailTypes";

/** "> " before each line of the message answered. */
export const quoteText = (text: string): string =>
  text
    .replace(/\s+$/, "")
    .split(/\r?\n/)
    .map((line) => `> ${line}`)
    .join("\n");

export const forwardSubject = (subject: string | null): string => {
  const value = (subject ?? "").trim();
  return /^(fwd?|tr)\s*:/i.test(value) ? value : `Fwd: ${value}`.trim();
};

const threadOf = (message: MailboxMessage) => ({
  in_reply_to: message.message_id ?? undefined,
  references: [
    ...message.references,
    ...(message.message_id ? [message.message_id] : []),
  ],
});

/**
 * Reply to the sender (to the recipients of a message the user sent), or to
 * everyone with `all`. `header` reads like « On …, Paul wrote: ».
 */
export const replyDraft = (
  message: MailboxMessage,
  { all, ownEmail, header }: { all: boolean; ownEmail: string; header: string },
): EmailDraft => {
  const own = ownEmail.toLowerCase();
  const isOwnMessage = message.from?.email === own;
  const to = isOwnMessage ? message.to : message.from ? [message.from] : [];
  const toEmails = new Set(to.map((recipient) => recipient.email));
  const cc = all
    ? [...message.to, ...message.cc].filter(
        (recipient, index, list) =>
          recipient.email !== own &&
          !toEmails.has(recipient.email) &&
          list.findIndex((item) => item.email === recipient.email) === index,
      )
    : [];
  return {
    to,
    cc,
    subject: replySubject(message.subject),
    body: `\n\n${header}\n${quoteText(message.text ?? "")}`,
    links: {},
    ...threadOf(message),
  };
};

/** Forward to someone else, with the original files. */
export const forwardDraft = (
  message: MailboxMessage,
  { header, attachments }: { header: string; attachments: RAFile[] },
): EmailDraft => ({
  to: [],
  subject: forwardSubject(message.subject),
  body: `\n\n${header}\n\n${message.text ?? ""}`,
  links: {},
  attachments,
});
